/**
 * PropertyFeatureSection - Core Reusable Property Feature Component
 * 
 * A showcase section for highlighting property features with large imagery,
 * text content, and call-to-action buttons. Inspired by Airbnb's feature sections.
 * 
 * This is a SCORABLE component - theme-agnostic and reusable across all themes.
 */

'use client';

import React, { useState } from 'react';

export interface PropertyFeatureSectionProps {
  /**
   * Main heading text
   */
  heading: string;
  
  /**
   * Video/media section (top right)
   */
  videoSection?: {
    thumbnail: string;
    title: string;
    description: string;
    videoUrl?: string;
  };
  
  /**
   * Large featured property
   */
  mainProperty: {
    image: string;
    title: string;
    description: string;
    thumbnails?: string[];
    detailsLink?: string;
  };
  
  /**
   * Secondary property card
   */
  secondaryProperty?: {
    image: string;
    price: string;
    ctaText: string;
    ctaLink: string;
  };
  
  /**
   * Additional text content
   */
  additionalContent?: string;
  
  /**
   * Custom className
   */
  className?: string;
  
  /**
   * Enable carousel for multiple properties
   */
  enableCarousel?: boolean;
}

/**
 * Core Property Feature Section Component
 */
export function PropertyFeatureSection({
  heading,
  videoSection,
  mainProperty,
  secondaryProperty,
  additionalContent,
  className = '',
  enableCarousel = false,
}: PropertyFeatureSectionProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => prev + 1);
  };

  return (
    <section className={`property-feature-section ${className}`}>
      {/* Header with Video Section */}
      <div className="property-feature-header">
        <div className="property-feature-heading">
          <h2>{heading}</h2>
        </div>
        
        {videoSection && (
          <div className="property-feature-video">
            <div className="video-thumbnail">
              <div className="w-full h-full bg-gray-200" />
              <button className="play-button" aria-label="Play video">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            </div>
            <div className="video-info">
              <h3>{videoSection.title}</h3>
              <p>{videoSection.description}</p>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="property-feature-content">
        {/* Large Property Image */}
        <div className="main-property">
          <div className="main-property-image">
            <div className="w-full h-full bg-gray-200" />
            
            {/* Thumbnails */}
            {mainProperty.thumbnails && mainProperty.thumbnails.length > 0 && (
              <div className="property-thumbnails">
                {mainProperty.thumbnails.map((_thumb, index) => (
                  <button key={index} className="thumbnail-button">
                    <div className="w-full h-full bg-gray-300" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Center Content */}
        <div className="center-content">
          <h3>{mainProperty.title}</h3>
          <p>{mainProperty.description}</p>
          
          {mainProperty.detailsLink && (
            <button className="details-button">
              Details
            </button>
          )}
          
          {additionalContent && (
            <p className="additional-content">{additionalContent}</p>
          )}
        </div>

        {/* Secondary Property Card */}
        {secondaryProperty && (
          <div className="secondary-property">
            <div className="secondary-property-image">
              <div className="w-full h-full bg-gray-200" />
            </div>
            <div className="secondary-property-info">
              <p className="price">{secondaryProperty.price}</p>
              <a href={secondaryProperty.ctaLink} className="cta-button">
                {secondaryProperty.ctaText}
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H2" />
                </svg>
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Arrows */}
      {enableCarousel && (
        <div className="carousel-navigation">
          <button 
            onClick={handlePrevSlide}
            className="nav-button"
            aria-label="Previous"
            disabled={currentSlide === 0}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button 
            onClick={handleNextSlide}
            className="nav-button"
            aria-label="Next"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}

